/**
 * KintsugiScrollVein — Decorative animated gold cracks that draw themselves
 * when the section enters the viewport (IntersectionObserver).
 *
 * Usage: place inside a `position: relative` parent. Purely decorative
 * (pointer-events none, aria-hidden).
 *
 * Props:
 *   variant?: "diagonal" | "horizontal" | "s-curve" | "corner-tl" | "corner-br"
 *   opacity?: number (default 0.75)
 *   strokes?: 1 | 2 | 3     — how many parallel veins to draw
 *   speed?: number in seconds (default 3.2s per vein)
 *   className?: string      — extra classes for the wrapping svg
 */
import { useEffect, useRef, useState } from "react";

const PATHS = {
  diagonal: [
    "M -20 60 Q 300 40, 560 200 T 1000 340 Q 1200 420, 1460 360",
    "M -20 720 Q 260 660, 500 700 T 860 620 Q 1080 580, 1460 640",
    "M 100 380 Q 300 420, 520 380 T 900 420",
  ],
  horizontal: [
    "M -20 180 Q 300 140, 560 200 T 1000 180 Q 1200 160, 1460 200",
    "M -20 560 Q 300 620, 580 580 T 1000 600 Q 1200 620, 1460 580",
  ],
  "s-curve": [
    "M -20 100 Q 400 140, 720 400 T 1460 700",
    "M -20 700 Q 400 660, 720 400 T 1460 100",
  ],
  "corner-tl": [
    "M -20 -10 Q 200 40, 340 200 T 620 500 Q 780 640, 900 800",
    "M -20 40 Q 120 100, 240 200 T 460 400",
  ],
  "corner-br": [
    "M 900 -10 Q 1200 60, 1300 200 T 1460 500 Q 1500 640, 1460 810",
    "M 1000 100 Q 1200 200, 1300 320 T 1460 500",
  ],
};

export default function KintsugiScrollVein({
  variant = "diagonal",
  opacity = 0.75,
  strokes = 3,
  speed = 3.2,
  className = "",
}) {
  const svgRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const paths = (PATHS[variant] || PATHS.diagonal).slice(0, strokes);

  useEffect(() => {
    const node = svgRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "-40px 0px -40px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const gradientId = `kv-grad-${variant}-${strokes}`;

  return (
    <svg
      ref={svgRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      viewBox="0 0 1440 800"
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ zIndex: 0, opacity }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#B57420" />
          <stop offset="30%" stopColor="#F5C370" />
          <stop offset="55%" stopColor="#FFE29A" />
          <stop offset="75%" stopColor="#D99533" />
          <stop offset="100%" stopColor="#7A4A14" />
        </linearGradient>
      </defs>
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeLinecap="round"
          strokeWidth={i === 0 ? 1.5 : i === 1 ? 1 : 0.7}
          style={{
            strokeDasharray: 2000,
            strokeDashoffset: visible ? 0 : 2000,
            transition: `stroke-dashoffset ${speed}s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.5}s`,
            filter: "drop-shadow(0 0 6px rgba(245,195,112,0.7))",
            opacity: i === 0 ? 1 : i === 1 ? 0.7 : 0.5,
          }}
        />
      ))}
    </svg>
  );
}
